// src/shared/ui/components/choice-list.tsx
// A short single-choice list of cards (the approved design's PaChoiceList): a radio dot, the primary line, a quiet facts
// line, an optional badge on the chosen card ("Parent"). The chosen card takes the brand edge and the active-blue tint.
// Presentational: the caller owns the selection and the handler. Look: showcase-patient.css `/* choice list */`.
import type { ReactNode } from 'react';

export interface ChoiceItem {
  id: string;
  primary: ReactNode;
  secondary?: ReactNode;
  /** Shown after the primary line while this item is the chosen one. */
  badge?: ReactNode;
  disabled?: boolean;
  /** Passed to the facts line (tests and tours). */
  secondaryTestId?: string;
}

export interface ChoiceListProps {
  items: ChoiceItem[];
  selectedId: string | null | undefined;
  onSelect: (id: string) => void;
  label: string;
  disabled?: boolean;
}

export function ChoiceList({ items, selectedId, onSelect, label, disabled = false }: ChoiceListProps) {
  return (
    <ul data-slot="choice-list" role="radiogroup" aria-label={label}>
      {items.map((it) => {
        const on = it.id === selectedId;
        return (
          <li key={it.id}>
            <button
              type="button"
              role="radio"
              aria-checked={on}
              data-slot="choice-item"
              data-on={on ? 'true' : undefined}
              disabled={disabled || it.disabled}
              onClick={() => onSelect(it.id)}
            >
              <span data-slot="choice-dot" aria-hidden="true">
                {on ? <span data-slot="choice-dot-fill" /> : null}
              </span>
              <span data-slot="choice-text">
                <span data-slot="choice-primary">
                  {it.primary}
                  {on && it.badge ? <span data-slot="choice-badge">{it.badge}</span> : null}
                </span>
                {it.secondary != null ? (
                  <span data-slot="choice-secondary" data-testid={it.secondaryTestId}>
                    {it.secondary}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
